import { useEffect, useState } from 'react';
import { api } from './api';
import POSTS from './blog/meta.json';
import TOOL_META from './tools/meta.json';

const HEIGHT = 160;
const PERIODS = [[7, 'Last 7 days'], [30, 'Last 30 days'], [90, 'Last 90 days'], [365, 'Last 12 months'], [400, 'All time']];

function duration(seconds) {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${String(s % 60).padStart(2, '0')}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

function countryName(code) {
  if (!code || code === 'Unknown' || code === 'XX' || code === 'T1') return 'Unknown';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
}

function barPath(x, y, w, h) {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

const dayLabel = (day) => new Date(`${day}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const GROUPS = [['day', 'Day'], ['week', 'Week'], ['month', 'Month']];
const bucketLabel = (b, group) => (group === 'month'
  ? new Date(`${b.day}T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  : group === 'week' ? `${dayLabel(b.day)} – ${dayLabel(b.to)}` : dayLabel(b.day));
const shortLabel = (b, group) => (group === 'month'
  ? new Date(`${b.day}T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  : dayLabel(b.day));
// The browser's time zone, so the server counts days from the admin's midnight.
const TZ = new Date().getTimezoneOffset();
// Whether "Hide likely robots" is on, read by the visit lists (set by AdminAnalytics each render).
const HUMANS = { on: false };

// Visitors by day, week or month as bars; the tooltip adds sign-ups. Clicking a bar lists its visitors.
function VisitorChart({ report, group, setGroup, picked, onPick }) {
  const [hovered, setHovered] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const rows = (group === 'week' ? report.weekly : group === 'month' ? report.monthly : null) || report.daily.map((d) => ({ ...d, to: d.day }));
  const max = Math.max(1, ...rows.map((d) => d.visitors));
  const width = 600;
  const slot = width / rows.length;
  const unit = group === 'day' ? 'day' : group;
  const isPicked = (b) => picked && picked.from === b.day && picked.to === b.to;
  const pick = (b) => onPick(isPicked(b) ? null : { from: b.day, to: b.to, label: bucketLabel(b, group) });

  return (
    <section className="admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2>Visitors per {unit}</h2>
          <p className="admin-panel-sub">Different people each {unit} &bull; click a bar to see only that {unit} in every number and card</p>
        </div>
        <div className="vc-controls">
          <div className="inbox-filter" role="group" aria-label="Group by">
            {GROUPS.map(([id, label]) => (
              <button key={id} type="button" className={`secondary small${group === id ? ' active' : ''}`} aria-pressed={group === id}
                onClick={() => { setGroup(id); setHovered(null); onPick(null); }}>
                {label}
              </button>
            ))}
          </div>
          <button type="button" className="secondary small" onClick={() => setShowTable(!showTable)}>
            {showTable ? 'Show chart' : 'Show table'}
          </button>
        </div>
      </div>
      {showTable ? (
        <div className="table-wrap admin-chart-table">
          <table>
            <thead><tr><th>{GROUPS.find(([id]) => id === group)[1]}</th><th>Visitors</th><th>Sign-ups</th><th /></tr></thead>
            <tbody>
              {[...rows].reverse().map((d) => (
                <tr key={d.day} className={isPicked(d) ? 'vc-picked' : ''}>
                  <td>{bucketLabel(d, group)}</td><td>{d.visitors}</td><td>{d.signups}</td>
                  <td>{d.visitors > 0 && <button type="button" className="link-button" onClick={() => pick(d)}>{isPicked(d) ? 'Showing' : 'See visitors'}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-chart" onMouseLeave={() => setHovered(null)}>
          <div className="admin-chart-ylabels"><span>{max}</span><span>0</span></div>
          <div className="admin-chart-plot">
            <svg viewBox={`0 0 ${width} ${HEIGHT}`} preserveAspectRatio="none" role="group"
              aria-label={`Visitors per ${unit}, ${rows.length} bars. Choose a bar to list its visitors.`}>
              <line x1="0" x2={width} y1={HEIGHT / 2} y2={HEIGHT / 2} className="admin-chart-grid" />
              {rows.map((d, i) => {
                const h = (d.visitors / max) * (HEIGHT - 4);
                return (
                  <g key={d.day}>
                    {d.visitors > 0 && (
                      <path d={barPath(i * slot + 1, HEIGHT - h, Math.max(1, slot - 2), h)}
                        className={`admin-chart-bar${hovered === i ? ' hovered' : ''}${isPicked(d) ? ' picked' : ''}`} />
                    )}
                    <rect x={i * slot} y="0" width={slot} height={HEIGHT} fill="transparent" className="vc-hit"
                      role="button" tabIndex={d.visitors > 0 ? 0 : -1} aria-pressed={isPicked(d)}
                      aria-label={`${bucketLabel(d, group)}: ${d.visitors} visitors, ${d.signups} sign-ups`}
                      onMouseEnter={() => setHovered(i)} onFocus={() => setHovered(i)}
                      onClick={() => d.visitors > 0 && pick(d)}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && d.visitors > 0) { e.preventDefault(); pick(d); } }} />
                  </g>
                );
              })}
              <line x1="0" x2={width} y1={HEIGHT - 0.5} y2={HEIGHT - 0.5} className="admin-chart-axis" />
            </svg>
            {hovered !== null && rows[hovered] && (
              <div className={`admin-chart-tooltip${hovered > rows.length * 0.85 ? ' align-right' : ''}${hovered < rows.length * 0.15 ? ' align-left' : ''}`}
                style={{ left: `${((hovered + 0.5) / rows.length) * 100}%` }}>
                <div><strong>{rows[hovered].visitors}</strong> visitor{rows[hovered].visitors === 1 ? '' : 's'}</div>
                <div><strong>{rows[hovered].signups}</strong> sign-up{rows[hovered].signups === 1 ? '' : 's'}</div>
                <span>{bucketLabel(rows[hovered], group)}</span>
              </div>
            )}
            <div className="admin-chart-xlabels">
              <span>{shortLabel(rows[0], group)}</span>
              {rows.length > 2 && <span>{shortLabel(rows[Math.floor(rows.length / 2)], group)}</span>}
              {rows.length > 1 && <span>{group === 'day' ? 'Today' : shortLabel(rows[rows.length - 1], group)}</span>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// "🇲🇽" from "MX".
const flag = (code) => (/^[A-Z]{2}$/.test(code || '') && code !== 'XX' && code !== 'T1'
  ? String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0))) : '🌐');
const ordinal = (n) => `${n}${['th', 'st', 'nd', 'rd'][(n % 100 > 10 && n % 100 < 14) || n % 10 > 3 ? 0 : n % 10]}`;
const clock = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const when = (d) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const articleTitle = (slug) => (POSTS.find((p) => p.slug === slug) || { en: { title: slug } }).en.title;
const toolName = (slug) => (TOOL_META.find((m) => m.slug === slug) || { en: { name: slug } }).en.name;

const VISIT_FILTERS = [
  ['all', 'All', () => true],
  ['new', 'First visit', (v) => v.visitNumber === 1],
  ['back', 'Came back', (v) => v.visitNumber > 1],
  ['signup', 'Signed up', (v) => v.signedUp],
  ['member', 'Members', (v) => v.member],
  ['long', 'Stayed 1 min+', (v) => v.seconds >= 60],
];

// What happened during the visit, as short tags.
function visitTags(v) {
  return [
    v.signedUp && ['good', 'Signed up'],
    v.member && !v.signedUp && ['', `Member: ${v.member}`],
    v.member && v.signedUp && ['', v.member],
    v.lead && ['good', 'Got the free guide'],
    v.demo && ['', v.demoSignup ? 'Demo → clicked sign up' : 'Opened the demo'],
    (v.reportUpload || v.reportSample) && ['', v.reportUpload ? 'Report card: own file' : 'Report card: sample'],
    v.visitNumber > 1 && ['', `${ordinal(v.visitNumber)} visit`],
    v.active === false && ['robot', 'No activity: likely a robot'],
  ].filter(Boolean);
}

function VisitDetail({ v }) {
  const trail = v.trail && v.trail.length ? v.trail : null;
  return (
    <div className="vl-detail">
      <dl className="vl-facts">
        <div><dt>Came from</dt><dd>{v.source || 'Direct'}{v.referrer && v.referrer !== v.source ? ` (${v.referrer})` : ''}{v.campaign ? ` · post “${v.campaign}”` : ''}</dd></div>
        <div><dt>First page</dt><dd>{pageName(v.landing || '/')}</dd></div>
        <div><dt>Time on site</dt><dd>{duration(v.seconds || 0)}</dd></div>
        <div><dt>This browser</dt><dd>{v.visitsInAll === 1 ? 'Only this visit' : `${ordinal(v.visitNumber)} of ${v.visitsInAll} visits`}</dd></div>
      </dl>
      <h4>Pages, in order</h4>
      {trail ? (
        <ol className="vl-trail">
          {trail.map((t, i) => (
            <li key={`${t.p}-${i}`}>
              <span className="vl-trail-time">{clock(t.at)}</span>
              <span className="vl-trail-page">{pageName(t.p)}</span>
              <span className="vl-trail-secs">{t.s ? duration(t.s) : '< 15s'}</span>
            </li>
          ))}
        </ol>
      ) : (
        <>
          <ul className="vl-pages">{(v.paths || [v.landing || '/']).map((p) => <li key={p}>{pageName(p)}</li>)}</ul>
          <p className="field-hint">Order and time per page are recorded for visits from October 27, 2026 on.</p>
        </>
      )}
      {v.articles.length > 0 && (
        <>
          <h4>Articles read</h4>
          <ul className="vl-pages">
            {v.articles.map((a) => <li key={a.slug}>{articleTitle(a.slug)} <em>{duration(a.seconds)} · {a.depth === 100 ? 'finished' : `read ${a.depth}%`}</em></li>)}
          </ul>
        </>
      )}
      {(v.clicks || []).length > 0 && (
        <>
          <h4>Buttons clicked</h4>
          <ul className="vl-pages">{v.clicks.map((c) => <li key={c}>{CLICK_NAMES[c] || c}</li>)}</ul>
        </>
      )}
      {(v.tools || []).length > 0 && (
        <>
          <h4>Calculators used</h4>
          <ul className="vl-pages">{v.tools.map((slug) => <li key={slug}>{toolName(slug)}</li>)}</ul>
        </>
      )}
    </div>
  );
}

// The visits in a range (the picked bar or the whole period), newest first, optionally only
// those behind one row of a card (`by` the card, `value` the row). Click one for its journey.
const STEP = 25;
function VisitorList({ range, by = '', value = '', compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(STEP);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(null);

  useEffect(() => { setLimit(STEP); setOpen(null); }, [range.from, range.to, by, value]);
  useEffect(() => {
    let live = true;
    setError('');
    const q = new URLSearchParams({ from: range.from, to: range.to, limit, tz: TZ, ...(by ? { by, value } : {}), ...(HUMANS.on ? { humans: '1' } : {}) });
    api(`/t/visitors?${q}`).then((d) => live && setData(d)).catch((err) => live && setError(err.message));
    return () => { live = false; };
  }, [range.from, range.to, by, value, limit, HUMANS.on]); // eslint-disable-line react-hooks/exhaustive-deps

  const test = VISIT_FILTERS.find(([id]) => id === filter)[2];
  const visits = data ? data.visits.filter(test) : [];
  const Wrap = compact ? 'div' : 'section';

  return (
    <Wrap className={compact ? 'vl vl-compact' : 'vl'}>
      <div className="admin-panel-header">
        <div>
          {!compact && <h2>Every visit: {range.label}</h2>}
          <p className="admin-panel-sub">
            {data ? `${data.total} visit${data.total === 1 ? '' : 's'}` : 'Loading…'} &bull; newest first &bull; click a visit to see its pages in order
          </p>
        </div>
        <div className="inbox-filter" role="group" aria-label="Show">
          {VISIT_FILTERS.map(([id, label]) => (
            <button key={id} type="button" className={`secondary small${filter === id ? ' active' : ''}`} aria-pressed={filter === id} onClick={() => setFilter(id)}>{label}</button>
          ))}
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {data && !visits.length && <p className="empty">No visits here{filter !== 'all' ? ' that match' : ''}.</p>}
      {visits.length > 0 && (
        <ul className="vl-list">
          {visits.map((v) => {
            const isOpen = open === v.sid;
            return (
              <li key={v.sid} className={isOpen ? 'open' : ''}>
                <button type="button" className="vl-row" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : v.sid)}>
                  <span className="vl-flag" aria-hidden="true">{flag(v.country)}</span>
                  <span className="vl-main">
                    <span className="vl-line">
                      <strong>{countryName(v.country)}</strong>
                      <span>{v.device || 'Computer'} · {v.lang === 'es' ? 'Spanish' : 'English'}</span>
                      <span className="vl-when">{when(v.start)}</span>
                    </span>
                    <span className="vl-line vl-sub">
                      <span>from <b>{v.source || 'Direct'}</b></span>
                      <span>{duration(v.seconds || 0)}</span>
                      <span>{v.pages} page{v.pages === 1 ? '' : 's'}</span>
                      <span className="vl-landing">{pageName(v.landing || '/')}</span>
                    </span>
                    {visitTags(v).length > 0 && (
                      <span className="vl-tags">{visitTags(v).map(([tone, label]) => <span key={label} className={`vl-tag ${tone}`}>{label}</span>)}</span>
                    )}
                  </span>
                  <span className={`panel-toggle-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">&#9656;</span>
                </button>
                {isOpen && <VisitDetail v={v} />}
              </li>
            );
          })}
        </ul>
      )}
      {data && data.visits.length < data.total && (
        <button type="button" className="secondary small vl-more" onClick={() => setLimit((n) => Math.min(n + STEP * 2, 500))} disabled={limit >= 500}>
          {limit >= 500 ? 'Showing the newest 500' : `Show more (${data.total - data.visits.length} older)`}
        </button>
      )}
    </Wrap>
  );
}

// Every visit in the range, folded until opened.
function AllVisits({ range, count }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="admin-panel">
      <h2>
        <button type="button" className="panel-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <span className={`panel-toggle-chevron${open ? ' open' : ''}`} aria-hidden="true">&#9656;</span>
          Every visit
          <span className="panel-toggle-count">{count} &bull; {range.label}</span>
        </button>
      </h2>
      {open && <VisitorList range={range} compact />}
    </section>
  );
}

// A ranked list with a bar for share of the biggest row.
// Where a shared link is posted, and the tag that names it in "Where visitors come from".
const LINK_PLACES = [['x', 'X'], ['instagram', 'Instagram'], ['youtube', 'YouTube'], ['discord', 'Discord'], ['reddit', 'Reddit'],
  ['tiktok', 'TikTok'], ['facebook', 'Facebook'], ['whatsapp', 'WhatsApp'], ['email', 'Email'], ['friend', 'A friend or DM']];
const LINK_PAGES = [['/', 'Homepage'], ['/es', 'Spanish homepage'], ['/blog', 'Blog'], ['/learn', 'Learn'], ['/tools', 'Free calculators'],
  ['/guide', 'Free guide'], ['/signup', 'Sign-up page'], ...POSTS.map((p) => [`/blog/${p.slug}`, `Article: ${p.en.title}`])];

// Builds a link that shows up by name in the reports below, so you can see which posts bring people in.
function LinkMaker() {
  const [place, setPlace] = useState('x');
  const [page, setPage] = useState('/');
  const [post, setPost] = useState('');
  const [copied, setCopied] = useState('');
  const campaign = post.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  const link = `https://marketmatrixhq.com${page}?utm_source=${place}${campaign ? `&utm_campaign=${campaign}` : ''}`;
  const bios = LINK_PLACES.slice(0, 4).map(([id, name]) => [name, `https://marketmatrixhq.com/?utm_source=${id}&utm_campaign=bio`]);

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(''), 2000);
    }).catch(() => {});
  }

  return (
    <section className="admin-panel link-maker">
      <h2>Make a tracked link</h2>
      <p className="admin-panel-sub">Use a link like this in every post and bio. Visits and sign-ups from it show up below under its source, and under &ldquo;Posts and campaigns&rdquo; by the name you give the post.</p>
      <div className="link-maker-fields">
        <label>Where you're posting it
          <select value={place} onChange={(e) => setPlace(e.target.value)}>
            {LINK_PLACES.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </label>
        <label>Which page it opens
          <select value={page} onChange={(e) => setPage(e.target.value)}>
            {LINK_PAGES.map(([path, name]) => <option key={path} value={path}>{name}</option>)}
          </select>
        </label>
        <label>Name this post (optional)
          <input value={post} onChange={(e) => setPost(e.target.value)} placeholder="e.g. consistency rule thread" maxLength={80} />
        </label>
      </div>
      <div className="link-maker-result">
        <code>{link}</code>
        <button type="button" className="small" onClick={() => copy(link)}>{copied === link ? 'Copied' : 'Copy link'}</button>
      </div>
      <h3>Links for your profiles</h3>
      <ul className="link-maker-bios">
        {bios.map(([name, url]) => (
          <li key={name}>
            <span>{name} bio</span>
            <code>{url}</code>
            <button type="button" className="secondary small" onClick={() => copy(url)}>{copied === url ? 'Copied' : 'Copy'}</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// With `filter` (row → [card, value] for /api/t/visitors), clicking a row lists those visits under it.
function RankList({ title, sub, rows, label = (r) => r.name, value = (r) => r.visits, extra, empty = 'No data yet.', filter, range }) {
  const [open, setOpen] = useState(null);
  const from = range && range.from;
  const to = range && range.to;
  useEffect(() => setOpen(null), [from, to]);
  const max = Math.max(1, ...rows.map(value));
  return (
    <section className="admin-panel analytics-list">
      <h2>{title}</h2>
      {sub && <p className="admin-panel-sub">{sub}</p>}
      {rows.length ? (
        <ul>
          {rows.map((r) => {
            const key = label(r);
            const isOpen = open === key;
            const body = (
              <>
                <div className="analytics-row">
                  <span className="analytics-name">{filter && <span className={`panel-toggle-chevron${isOpen ? ' open' : ''}`} aria-hidden="true">&#9656;</span>}{key}</span>
                  <span className="analytics-value">{value(r)}{extra && <em>{extra(r)}</em>}</span>
                </div>
                <span className="analytics-bar"><span style={{ width: `${(value(r) / max) * 100}%` }} /></span>
              </>
            );
            const [by, val] = filter ? filter(r) : [];
            return (
              <li key={key} className={isOpen ? 'open' : ''}>
                {filter ? (
                  <button type="button" className="analytics-pick" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : key)}>{body}</button>
                ) : body}
                {isOpen && <VisitorList range={range} by={by} value={val} compact />}
              </li>
            );
          })}
        </ul>
      ) : <p className="empty">{empty}</p>}
    </section>
  );
}

// Readable names for the tracked buttons (CLICK_IDS on the server) and pages.
const CLICK_NAMES = {
  'header-signup': 'Header: Sign up free',
  'hero-signup': 'Homepage hero: Start your free journal',
  'hero-demo': 'Homepage hero: View demo',
  'hero-report-card': 'Homepage hero: Free report card link',
  'tour-demo': 'Homepage tour: Open in the demo',
  'benefit-demo': 'Homepage benefits: See it in the demo',
  'band-demo': 'Homepage demo band: View demo',
  'final-signup': 'Homepage bottom: Start your free journal',
  'final-demo': 'Homepage bottom: View demo',
};
function pageName(path) {
  const es = /^\/es(\/|$)/.test(path);
  const p = es ? path.slice(3) || '/' : path;
  const article = p.startsWith('/blog/') && POSTS.find((x) => x.slug === p.slice(6));
  const name = ({ '/': 'Homepage', '/app': 'Journal: Trades', '/signup': 'Sign up', '/login': 'Log in', '/pricing': 'Pricing', '/blog': 'Blog' }[p]
    || (article && `Article: ${article.en.title}`)
    || (p.startsWith('/tools/') && `Calculator: ${toolName(p.slice(7))}`)
    || (p.startsWith('/app/') ? `Journal: ${p.slice(5).replace(/^./, (c) => c.toUpperCase())}` : p));
  return es ? `${name} (Spanish)` : name;
}

const signupNote = (r) => (r.signups ? ` · ${r.signups} sign-up${r.signups === 1 ? '' : 's'}` : '');

const ARTICLE_SORTS = [
  ['views', 'Most viewed', (a) => a.views],
  ['time', 'Longest read', (a) => a.avgSeconds],
  ['finished', 'Most finished', (a) => (a.views ? a.finished / a.views : 0)],
];

// Which blog articles get read, for how long, and how far.
const ARTICLES_OPEN_KEY = 'mm-admin-articles-open';

function ArticleStats({ articles, scope, range }) {
  const [sort, setSort] = useState('views');
  const [picked, setPicked] = useState(null);
  // Open or folded, remembered on this browser.
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(ARTICLES_OPEN_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  if (!articles) return null;
  const totalViews = articles.reduce((sum, a) => sum + a.views, 0);

  function toggle() {
    setOpen((o) => {
      try {
        localStorage.setItem(ARTICLES_OPEN_KEY, o ? 'closed' : 'open');
      } catch {
        // Not remembered in private mode.
      }
      return !o;
    });
  }
  const title = (slug) => (POSTS.find((p) => p.slug === slug) || { en: { title: slug } }).en.title;
  const key = ARTICLE_SORTS.find(([id]) => id === sort)[2];
  const rows = [...articles].sort((a, b) => key(b) - key(a));
  const pct = (n, of) => (of ? `${Math.round((n / of) * 100)}%` : '–');

  return (
    <section className="admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2>
            <button type="button" className="panel-toggle" aria-expanded={open} aria-controls="article-stats-body" onClick={toggle}>
              <span className={`panel-toggle-chevron${open ? ' open' : ''}`} aria-hidden="true">&#9656;</span>
              Articles
              <span className="panel-toggle-count">{articles.length} read &bull; {totalViews} views</span>
            </button>
          </h2>
          {open && (
            <p className="admin-panel-sub">
              {scope} &bull; click an article to see who read it &bull; time counts only while the article is on screen &bull; &ldquo;finished&rdquo; means they scrolled to the end of the article
            </p>
          )}
        </div>
        {open && (
          <div className="inbox-filter" role="group" aria-label="Sort articles">
            {ARTICLE_SORTS.map(([id, label]) => (
              <button key={id} type="button" className={`secondary small${sort === id ? ' active' : ''}`} onClick={() => setSort(id)}>{label}</button>
            ))}
          </div>
        )}
      </div>
      {!open ? null : rows.length ? (
        <div className="table-wrap article-stats-scroll" id="article-stats-body">
          <table className="article-stats">
            <thead>
              <tr><th>Article</th><th>Views</th><th>Readers</th><th>Avg time</th><th>Read half</th><th>Finished</th><th>Guide sign-ups</th><th>Joined</th></tr>
            </thead>
            <tbody>
              {rows.map((a) => [
                <tr key={a.slug} className={`article-pick${picked === a.slug ? ' open' : ''}`} onClick={() => setPicked(picked === a.slug ? null : a.slug)}>
                  <td className="article-stats-title">
                    <button type="button" className="link-button" aria-expanded={picked === a.slug}>
                      <span className={`panel-toggle-chevron${picked === a.slug ? ' open' : ''}`} aria-hidden="true">&#9656;</span>{title(a.slug)}
                    </button>
                  </td>
                  <td>{a.views}</td>
                  <td>{a.readers}</td>
                  <td>{duration(a.avgSeconds)}</td>
                  <td>{pct(a.half, a.views)}</td>
                  <td>{pct(a.finished, a.views)}</td>
                  <td>{a.leads}</td>
                  <td>{a.signups}</td>
                </tr>,
                picked === a.slug && (
                  <tr key={`${a.slug}-visits`} className="article-visits"><td colSpan={8}><VisitorList range={range} by="article" value={a.slug} compact /></td></tr>
                ),
              ])}
            </tbody>
          </table>
        </div>
      ) : <p className="empty">No article reads in this period yet.</p>}
    </section>
  );
}

// Whether the free report card (/report-card) turns visitors into members.
function ReportCardStats({ stats, scope }) {
  if (!stats) return null;
  const tried = stats.samples + stats.uploads;
  return (
    <section className="admin-panel analytics-list">
      <h2>Free report card</h2>
      <p className="admin-panel-sub">Visits that used /report-card &bull; {scope}</p>
      <section className="summary admin-summary">
        {[
          { label: 'Went back to the sample', value: stats.samples, note: 'the sample opens by itself' },
          { label: 'Used their own file', value: stats.uploads },
          { label: 'Clicked save / sign up', value: stats.signupClicks },
          { label: 'Asked for Monday reminders', value: stats.reminders || 0 },
          { label: 'Signed up after', value: stats.signups, note: tried ? `${((stats.signups / tried) * 100).toFixed(1)}% of visits that tried it` : undefined },
        ].map((tile) => (
          <div key={tile.label} className="stat">
            <span className="stat-label">{tile.label}</span>
            <span className="stat-value">{tile.value}</span>
            {tile.note && <span className="stat-note">{tile.note}</span>}
          </div>
        ))}
      </section>
    </section>
  );
}

// Whether the demo turns visitors into members. Lots of demo visits but few sign-ups
// means the sample isn't convincing, not that the idea is wrong.
function DemoStats({ demo, scope }) {
  if (!demo) return null;
  const rate = demo.visits ? (demo.signups / demo.visits) * 100 : 0;
  return (
    <section className="admin-panel analytics-list">
      <h2>Demo</h2>
      <p className="admin-panel-sub">Visits that opened the sample account &bull; {scope}</p>
      <section className="summary admin-summary">
        {[
          { label: 'Opened the demo', value: demo.visits },
          { label: 'Explored it', value: demo.explored || 0, note: 'clicked a "See:" link' },
          { label: 'Tried to import or save', value: demo.prompts, note: 'and saw the sign-up prompt' },
          { label: 'Clicked sign up', value: demo.signupClicks || 0, note: 'inside the demo' },
          { label: 'Signed up after', value: demo.signups, note: `${rate.toFixed(1)}% of demo visits` },
        ].map((tile) => (
          <div key={tile.label} className="stat">
            <span className="stat-label">{tile.label}</span>
            <span className="stat-value">{tile.value}</span>
            {tile.note && <span className="stat-note">{tile.note}</span>}
          </div>
        ))}
      </section>
    </section>
  );
}

// New members, and how many of them got as far as recording trades.
function ActivationStats({ activation, scope }) {
  if (!activation) return null;
  const rate = activation.newMembers ? (activation.activated / activation.newMembers) * 100 : 0;
  return (
    <section className="admin-panel analytics-list">
      <h2>New members</h2>
      <p className="admin-panel-sub">Members who joined, and how many imported or added trades &bull; {scope}</p>
      <section className="summary admin-summary">
        {[
          { label: 'Joined', value: activation.newMembers },
          { label: 'Recorded trades', value: activation.activated, note: `${rate.toFixed(1)}% of new members` },
        ].map((tile) => (
          <div key={tile.label} className="stat">
            <span className="stat-label">{tile.label}</span>
            <span className="stat-value">{tile.value}</span>
            {tile.note && <span className="stat-note">{tile.note}</span>}
          </div>
        ))}
      </section>
    </section>
  );
}

// Free guide sign-ups and how many became members.
function GuideStats({ days }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    api(`/guide/stats?days=${days}`).then(setStats).catch((err) => setError(err.message));
  }, [days]);

  const rate = stats && stats.total ? (stats.converted / stats.total) * 100 : 0;
  return (
    <section className="admin-panel analytics-list">
      <h2>Free guide</h2>
      <p className="admin-panel-sub">Email sign-ups for the Prop Challenge Survival Guide</p>
      {error && <p className="error">{error}</p>}
      {stats && (
        <>
          {!stats.followUpsOn && (
            <p className="analytics-warning">
              The guide email is sent, but the 3 follow-up emails are off until a mailing address is added (MAILING_ADDRESS on Render).
            </p>
          )}
          <section className="summary admin-summary">
            {[
              { label: `New (last ${days} days)`, value: stats.recent },
              { label: 'All subscribers', value: stats.total },
              { label: 'Became members', value: stats.converted, note: `${rate.toFixed(1)}% of subscribers` },
              { label: 'Unsubscribed', value: stats.unsubscribed },
            ].map((tile) => (
              <div key={tile.label} className="stat">
                <span className="stat-label">{tile.label}</span>
                <span className="stat-value">{tile.value}</span>
                {tile.note && <span className="stat-note">{tile.note}</span>}
              </div>
            ))}
          </section>
          {stats.bySource.length > 0 && (
            <ul className="analytics-members">
              {stats.bySource.map((s) => (
                <li key={s.name}>
                  <div className="analytics-row">
                    <span className="analytics-name">{s.name}</span>
                    <span className="analytics-value">{s.count}<em> sign-up{s.count === 1 ? '' : 's'}</em></span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [group, setGroup] = useState('day');
  const [picked, setPicked] = useState(null);

  const [loading, setLoading] = useState(false);
  // Hide visits with no scroll, tap, click or typing (likely robots), remembered on this browser.
  const [humans, setHumans] = useState(() => {
    try {
      return localStorage.getItem('mm-admin-humans') === '1';
    } catch {
      return false;
    }
  });
  HUMANS.on = humans;

  useEffect(() => { setPicked(null); }, [days]);
  // Picking a bar reloads every card for just that day, week or month; the chart keeps the whole period.
  useEffect(() => {
    let live = true;
    setError('');
    setLoading(true);
    const q = new URLSearchParams({ days, tz: TZ, ...(picked ? { from: picked.from, to: picked.to } : {}), ...(humans ? { humans: '1' } : {}) });
    api(`/t/report?${q}`).then((r) => live && setReport(r)).catch((err) => live && setError(err.message)).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [days, picked, humans]);
  const periodLabel = PERIODS.find(([p]) => p === days)[1];
  const whole = report && { from: report.daily[0].day, to: report.daily[report.daily.length - 1].day, label: periodLabel.toLowerCase() };
  const range = picked || whole;
  const scope = range && range.label.replace(/^./, (c) => c.toUpperCase());

  const t = report && report.totals;
  const tiles = t && [
    { label: 'Visitors', value: t.visitors, note: `${t.visits} visit${t.visits === 1 ? '' : 's'}` },
    { label: 'Avg time on site', value: duration(t.avgSeconds), note: 'per visit, while the page is open' },
    { label: 'Pages per visit', value: t.pagesPerVisit.toFixed(1) },
    { label: 'Sign-ups', value: t.signups },
    { label: 'Sign-up rate', value: `${t.conversion.toFixed(1)}%`, note: 'of visitors' },
    { label: 'Active members', value: t.activeMembers, note: 'used a feature' },
  ];

  return (
    <div className="analytics">
      <div className="admin-panel-header analytics-head">
        <div>
          <h2 className="analytics-title">Analytics</h2>
          <p className="admin-panel-sub">Visitors are anonymous. Your own visits aren&apos;t counted.</p>
        </div>
        <div className="inbox-filter" role="group" aria-label="Period">
          <button type="button" className={`secondary small${humans ? ' active' : ''}`} aria-pressed={humans}
            title="Leave out visits with no scroll, tap, click or typing. Recorded from October 28, 2026."
            onClick={() => setHumans((h) => { try { localStorage.setItem('mm-admin-humans', h ? '0' : '1'); } catch { /* not remembered */ } return !h; })}>
            {humans ? 'Robots hidden' : 'Hide likely robots'}
          </button>
          {PERIODS.map(([p, label]) => (
            <button key={p} type="button" className={`secondary small${days === p ? ' active' : ''}`} onClick={() => setDays(p)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {!report && !error && <p className="empty">Loading…</p>}
      {report && (
        <>
          {picked && (
            <p className="analytics-scope" role="status">
              <span>Showing <strong>{picked.label}</strong>. Every number and card on this page counts only this {group}; the chart still shows the {periodLabel.toLowerCase()}.</span>
              <button type="button" className="secondary small" onClick={() => setPicked(null)}>Show {periodLabel.toLowerCase()}</button>
            </p>
          )}
          <div className={loading ? 'analytics-loading' : ''}>
          <section className="summary admin-summary">
            {tiles.map((tile) => (
              <div key={tile.label} className="stat">
                <span className="stat-label">{tile.label}</span>
                <span className="stat-value">{tile.value}</span>
                {tile.note && <span className="stat-note">{tile.note}</span>}
              </div>
            ))}
          </section>
          </div>
          <VisitorChart report={report} group={group} setGroup={setGroup} picked={picked} onPick={setPicked} />
          <div className={loading ? 'analytics-loading' : ''}>
          <AllVisits range={range} count={`${t.visits} visit${t.visits === 1 ? '' : 's'}`} />
          <GuideStats days={days} />
          <ArticleStats articles={report.articles} scope={scope} range={range} />
          <DemoStats demo={report.demo} scope={scope} />
          <ReportCardStats stats={report.reportCard} scope={scope} />
          <ActivationStats activation={report.activation} scope={scope} />
          <p className="admin-panel-sub analytics-hint">Click any row in the cards below to see those visits: where they came from, every page in order and how long they stayed.</p>
          <div className="analytics-grid">
            <RankList title="Where visitors come from" sub="Visits by source, and how many signed up" rows={report.sources} extra={signupNote}
              range={range} filter={(r) => ['source', r.name]} />
            <RankList title="Landing pages" sub="The first page of each visit" rows={report.landings} extra={signupNote}
              label={(r) => pageName(r.name)} range={range} filter={(r) => ['landing', r.name]} />
            <RankList title="Countries" rows={report.countries} label={(r) => `${flag(r.name)} ${countryName(r.name)}`} extra={signupNote}
              range={range} filter={(r) => ['country', r.name]} />
            <RankList title="Devices & language" range={range} filter={(r) => r.filter}
              rows={[...report.devices.map((d) => ({ ...d, filter: ['device', d.name] })),
                ...report.languages.map((l) => ({ ...l, name: l.name === 'es' ? 'Spanish' : 'English', filter: ['lang', l.name] }))]} />
          </div>
          <div className="analytics-grid">
            <RankList title="Top pages" sub="Visits that opened each page (Spanish pages count with their English twin), and how many signed up"
              rows={report.topPages || []} label={(x) => pageName(x.path)} extra={signupNote} range={range} filter={(x) => ['page', x.path]} />
            <RankList title="Homepage buttons" sub="Visits that clicked each button, and how many of them signed up"
              rows={report.clicks || []} label={(x) => CLICK_NAMES[x.id] || x.id} extra={signupNote} empty="No button clicks recorded yet."
              range={range} filter={(x) => ['click', x.id]} />
            <RankList title="Free calculators" sub="Visits that opened each calculator, how many typed into it, and sign-ups after using it"
              rows={(report.tools || []).filter((x) => x.opened)} label={(x) => toolName(x.slug)} value={(x) => x.opened}
              extra={(x) => ` · ${x.used} used${x.signups ? ` · ${x.signups} sign-up${x.signups === 1 ? '' : 's'}` : ''}`}
              empty="No calculator opened in this period." range={range} filter={(x) => ['tool', x.slug]} />
          </div>
          <div className="analytics-grid">
            <RankList title="Posts and campaigns" sub="Visits from tracked links, by the name given to each post" rows={report.campaigns || []} extra={signupNote}
              empty="No tracked links used yet. Make one below." range={range} filter={(r) => ['campaign', r.name]} />
          </div>

          <LinkMaker />
          <div className="analytics-grid">
            <RankList title="Most-used features" sub="Uses in this period, and how many members used each" rows={report.features}
              value={(r) => r.uses} extra={(r) => ` · ${r.members} member${r.members === 1 ? '' : 's'}`} empty="No member activity in this period yet." />
            <section className="admin-panel analytics-list">
              <h2>Most active members</h2>
              <p className="admin-panel-sub">What each member uses most</p>
              {report.members.length ? (
                <ul className="analytics-members">
                  {report.members.map((m) => (
                    <li key={m.id}>
                      <div className="analytics-row">
                        <span className="analytics-name">{m.name}</span>
                        <span className="analytics-value">{m.uses}<em> uses</em></span>
                      </div>
                      <span className="analytics-tags">{m.features.map((f) => `${f.name} (${f.uses})`).join(' · ')}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="empty">No member activity in this period yet.</p>}
            </section>
          </div>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminAnalytics;
